import React, { ReactNode, useEffect, useRef } from 'react';
import { CrossIcon } from '@components/Icon';
import {
  useResponsiveValueV2,
  useWindowWidth
} from '@theme/DesignSystemProvider/BreakpointContext/useResponsiveValue';
import { useTooltip } from './useTooltip';
import {
  TooltipPosition,
  getMobileTooltipArrowPositionStyle,
  getMobileTooltipPositionStyle,
  getTooltipArrowClasses,
  getTooltipPositionClasses
} from './utility';

export type TooltipProps = {
  id?: string;
  children: ReactNode;
  content: ReactNode;
  position?: TooltipPosition;
  delay?: number;
  width?: string;
};

/**
 * Tooltip component that displays a tooltip with a delay when hovering over or clicking on the child element.
 *
 * @param {string} id - The ID of the toggle switch input element.
 * @param {ReactNode} children - The child element that triggers the tooltip.
 * @param {ReactNode} content - The content of the tooltip.
 * @param {TooltipPosition} position - The position of the tooltip (top, bottom, left, right).
 * @param {number} delay - The delay in milliseconds before showing the tooltip.
 * @param {string} width - The width of the tooltip.
 */
export const Tooltip = ({
  id = 'tooltip',
  children,
  content,
  position = 'top',
  delay = 300,
  width = 'w-64'
}: TooltipProps) => {
  const {
    isVisible,
    showTooltip,
    hideTooltip,
    toggleTooltip,
    computedPosition,
    tooltipContentRef,
    triggerRef
  } = useTooltip(delay, position);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isMobileScreen = useResponsiveValueV2() === 'mobile';
  const windowWidth = useWindowWidth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        event.target instanceof Node &&
        !tooltipRef.current.contains(event.target)
      ) {
        hideTooltip();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hideTooltip]);

  return (
    <div className="relative inline-block" ref={tooltipRef}>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleTooltip();
        }}
        className="inline-block cursor-pointer"
        aria-describedby={isVisible ? 'tooltip' : undefined}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipContentRef}
          id={id}
          role="tooltip"
          className={`
            absolute z-50
            ${getTooltipPositionClasses(computedPosition)}
            ${width}
            p-md rounded transition-opacity
            duration-${delay}
            bg-grey900 text-white text-md font-regular leading-md sm:pr-16
          `}
          style={
            isMobileScreen
              ? getMobileTooltipPositionStyle({
                  position: computedPosition,
                  windowWidth,
                  triggerElement: triggerRef.current!
                })
              : {}
          }
        >
          {content}
          <button
            type="button"
            onClick={hideTooltip}
            className="block md:hidden absolute top-8 right-8"
            aria-label="Close tooltip"
          >
            <CrossIcon size="xxsmall" />
          </button>
          <div
            className={`
              absolute
              border-solid
              ${getTooltipArrowClasses(computedPosition)}
            `}
            style={
              isMobileScreen
                ? getMobileTooltipArrowPositionStyle({
                    position: computedPosition,
                    triggerElement: triggerRef.current!
                  })
                : {}
            }
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
};
